/**
 * Tracks max scroll depth (25/50/75/100%) and time on page (15/30/60/180s).
 * Fires events at most once per threshold per mount.
 */
import { useEffect } from 'react';
import { track, type EventName } from '@/lib/analytics';

interface Options {
  /** Event name for scroll depth events. */
  scrollEvent?: EventName;
  /** Event name for time-on-page events. */
  timeEvent?: EventName;
  /** Extra props attached to every event. */
  props?: Record<string, unknown>;
}

const SCROLL_BUCKETS = [25, 50, 75, 100];
const TIME_BUCKETS = [15, 30, 60, 180];

export function useLandingFunnelMetrics({
  scrollEvent = 'landing_scroll_depth' as EventName,
  timeEvent = 'landing_time_on_page' as EventName,
  props = {},
}: Options = {}) {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const firedScroll = new Set<number>();
    const firedTime = new Set<number>();
    const startedAt = Date.now();
    let activeMs = 0;
    let lastTick = Date.now();
    let visible = document.visibilityState === 'visible';

    const onVisibility = () => {
      if (visible) activeMs += Date.now() - lastTick;
      visible = document.visibilityState === 'visible';
      lastTick = Date.now();
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const max = (doc.scrollHeight - doc.clientHeight) || 1;
      const pct = Math.min(100, Math.round((scrollTop / max) * 100));
      for (const bucket of SCROLL_BUCKETS) {
        if (pct >= bucket && !firedScroll.has(bucket)) {
          firedScroll.add(bucket);
          track(scrollEvent, { ...props, depth: bucket });
        }
      }
    };

    const interval = window.setInterval(() => {
      if (visible) activeMs += Date.now() - lastTick;
      lastTick = Date.now();
      const seconds = Math.round((Date.now() - startedAt) / 1000);
      const activeSeconds = Math.round(activeMs / 1000);
      for (const bucket of TIME_BUCKETS) {
        if (seconds >= bucket && !firedTime.has(bucket)) {
          firedTime.add(bucket);
          track(timeEvent, { ...props, seconds: bucket, active_seconds: activeSeconds });
        }
      }
    }, 5000);

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, [scrollEvent, timeEvent, props]);
}

/**
 * Fires `landing_section_viewed` (or a custom event) the first time an element
 * enters the viewport.
 */
export function useSectionViewed(
  ref: React.RefObject<HTMLElement>,
  section: string,
  eventName: EventName = 'landing_section_viewed' as EventName,
  extraProps: Record<string, unknown> = {},
) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let fired = false;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !fired) {
            fired = true;
            track(eventName, { section, ...extraProps });
            observer.disconnect();
          }
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, section, eventName, extraProps]);
}
